import React from "react";
import { useImmer } from "use-immer";

import { IgAlert } from "@intelligems/ig-theme";
import {
  GetProcessesResponseBodyType,
  ProcessType,
} from "@intelligems/ig-types";
import { Stack } from "@mui/material";
import Grid from "@mui/material/Grid";
import { HiddenErrorBoundary } from "@src/components/Utils/IgErrorBoundary";
import { useDispatch } from "@src/store";
import { dismissAlert } from "@src/store/slices/appSettings";
import { useQueryClient } from "@tanstack/react-query";

import { useGetOrg } from "../../../api/organizations/get-organization";
import { useDeleteProcess } from "../../../api/processes/delete-process";
import { useGetProcesses } from "../../../api/processes/get-processes";
import { processesKeys } from "../../../api/queryKeys";

interface ProcessesProps {
  filterToSpecificKey?: ProcessType;
  deleteOnOpen?: boolean;
}
export const Processes = ({
  filterToSpecificKey,
  deleteOnOpen,
}: ProcessesProps) => {
  const [isLoading, setIsLoading] = useImmer<Record<string, boolean>>({});
  const queryClient = useQueryClient();
  const organization = useGetOrg();
  const processes = useGetProcesses();
  const deleteProcess = useDeleteProcess();
  const dispatch = useDispatch();
  const handleDelete = (processId: string) => {
    setIsLoading((draft) => {
      draft[processId] = true;
      return draft;
    });

    queryClient.setQueryData<GetProcessesResponseBodyType>(
      processesKeys.one(organization.data.org.id),
      (data) => {
        return {
          processes: data?.processes.filter((p) => p.id !== processId) || [],
        };
      }
    );

    deleteProcess.mutate(
      {
        body: undefined,
        pathParams: { processId: processId },
        queryParams: undefined,
      },
      {
        onSettled: () => {
          setIsLoading((draft) => {
            draft[processId] = false;
          });
        },
      }
    );
  };

  const onDismiss = (alertKey: string) => {
    dispatch(
      dismissAlert({
        orgId: organization.data.org.id,
        alertId: alertKey,
      })
    );
  };

  if (!processes.isSuccess || !processes.data.processes.length) {
    return null;
  }

  return (
    <Stack
      direction={"column"}
      sx={{ marginBottom: "20px", width: "100%" }}
      spacing={1}
    >
      {processes.data.processes
        .filter((process) =>
          filterToSpecificKey ? process.type === filterToSpecificKey : process
        )
        .map((process) => {
          return (
            <Grid
              container
              key={process.id}
              sx={{ zIndex: 1299, width: "100%" }}
            >
              <HiddenErrorBoundary>
                <IgAlert
                  severity={process.severity}
                  title={process.title}
                  message={process.message}
                  loading={(!process.action && process.isLoading) || false}
                  loadingPercent={process.loadingPercent || undefined}
                  alertKey={`process-alert-${process.id}`}
                  onDismiss={() => {
                    handleDelete(process.id);
                    onDismiss(`process-alert-${process.id}`);
                  }}
                  primaryButton={
                    process.action
                      ? {
                          cta: process.actionMessage || "Ok",
                          onClick: () => {
                            if (process.action?.href) {
                              if (process.action.deleteOnOpen || deleteOnOpen) {
                                handleDelete(process.id);
                              }
                              window.open(process.action?.href, "_blank");
                            } else {
                              handleDelete(process.id);
                            }
                          },
                        }
                      : undefined
                  }
                />
              </HiddenErrorBoundary>
            </Grid>
          );
        })}
    </Stack>
  );
};
